import { createContext, useContext, useState, useCallback, type ReactNode } from "react";

export interface ImportJob {
  id: string;
  type: "url" | "youtube" | "document" | "research" | "google-drive";
  title: string;
  status: "pending" | "processing" | "extracting" | "analyzing" | "completed" | "failed";
  progress: number;
  error?: string;
  startedAt: Date;
  completedAt?: Date;
}

interface ImportProgressContextType {
  jobs: ImportJob[];
  activeJobs: ImportJob[];
  addJob: (job: Omit<ImportJob, "startedAt" | "progress">) => string;
  updateJob: (id: string, updates: Partial<ImportJob>) => void;
  removeJob: (id: string) => void;
  clearCompleted: () => void;
}

const ImportProgressContext = createContext<ImportProgressContextType | null>(null);

export function ImportProgressProvider({ children }: { children: ReactNode }) {
  const [jobs, setJobs] = useState<ImportJob[]>([]);

  const addJob = useCallback((jobData: Omit<ImportJob, "startedAt" | "progress">) => {
    const job: ImportJob = {
      ...jobData,
      progress: 0,
      startedAt: new Date(),
    };
    setJobs((prev) => [...prev, job]);
    return job.id;
  }, []);

  const updateJob = useCallback((id: string, updates: Partial<ImportJob>) => {
    setJobs((prev) =>
      prev.map((job) =>
        job.id === id
          ? {
              ...job,
              ...updates,
              completedAt:
                updates.status === "completed" || updates.status === "failed"
                  ? new Date()
                  : job.completedAt,
            }
          : job
      )
    );
  }, []);

  const removeJob = useCallback((id: string) => {
    setJobs((prev) => prev.filter((job) => job.id !== id));
  }, []);

  const clearCompleted = useCallback(() => {
    setJobs((prev) =>
      prev.filter((job) => job.status !== "completed" && job.status !== "failed")
    );
  }, []);

  const activeJobs = jobs.filter(
    (job) => job.status !== "completed" && job.status !== "failed"
  );

  return (
    <ImportProgressContext.Provider
      value={{ jobs, activeJobs, addJob, updateJob, removeJob, clearCompleted }}
    >
      {children}
    </ImportProgressContext.Provider>
  );
}

export function useImportProgress() {
  const context = useContext(ImportProgressContext);
  if (!context) {
    throw new Error("useImportProgress must be used within an ImportProgressProvider");
  }
  return context;
}
